import { store } from '@/app/store';
import {
  getField,
  screentimeAclKey,
  screentimeColourField,
  screentimeDetailsField,
  screentimeFields,
  screentimeLimitFields,
  screentimeTitleField,
  UrlDisabled,
  UrlInfo,
} from '@/features/channels/channelsSlice';


export const screenTimeUrl = "/screentime.json";

export const screentimeDaysDefault	= 7;
export const screentimeDaysMin		= 1;
export const screentimeDaysMax		= 35;

export const minutesPerDay = 1440;

export interface ScreenTimeInterval {
  from:					string;		// "HH:MM", inclusive
  to:					string;		// "HH:MM", inclusive
}

export interface ScreenTimeDay {
  day:					string;		// "YYYY-MM-DD"
  seconds:				number;
  intervals:			ScreenTimeInterval[];
}

export interface ScreenTimeReport {
  project_id:			string;
  room_no:				string;
  period:				string;
  quota_seconds:		number;		// stored minutes x 60. 0 = no quota.
  used_seconds:			number;
  remaining_seconds:	number;		// 0 also when there is NO quota - always
  								    // gate on quota_seconds > 0 before showing it.
  blocked:				boolean;
  days:					ScreenTimeDay[];	// newest last, gaps omitted
}

export type ScreenTimeResult =
  | { kind: "ok";					report: ScreenTimeReport }
  | { kind: "unauthorized" }							// 401 - session expired
  | { kind: "no_project_access" }						// 403 - any refusal of the read
  | { kind: "not_found" }								// 404 - project or room unknown
  | { kind: "bad_request";			status: number }	// 400 / 405 - bug in our request
  | { kind: "disabled" }								// 503 - recording off server-wide
  | { kind: "error";				status: number; message: string };	// 500 / network

export interface ScreenTimeRequest {
  projectId:			string;
  roomNo:				string;
  days?:				number;
  period?:				string;
}

export const screentimePeriodValues = ["day", "week", "month", "year"] as const;


export function normalizeScreentimePeriod(period?: string): string {
  return	period &&
			(screentimePeriodValues as readonly string[]).indexOf(period) > -1
			?	period
			:	"day";
}

export function clampScreentimeDays(days?: number): number {
  if (	days === undefined ||
		days === null ||
		!isFinite(days)
	 )
    return screentimeDaysDefault;

  return Math.min(screentimeDaysMax, Math.max(screentimeDaysMin, Math.floor(days)));
}

export function parseHHMM(value?: string): number | null {
  if (	!value ||
		value === UrlDisabled
	 )
    return null;

  const m = /^([0-9]{1,2}):([0-9]{2})$/.exec(value.trim());
  if (!m)
    return null;

  const hours = parseInt(m[1], 10);
  const mins  = parseInt(m[2], 10);

  if (	hours < 0 || hours > 23 ||
		mins  < 0 || mins  > 59
	 )
    return null;

  return hours * 60 + mins;
}


export function parseQuotaMinutes(value?: string): number {
  if (	!value ||
		value === UrlDisabled
	 )
    return 0;

  if (!/^[0-9]+$/.test(value.trim()))
    return 0;

  const n = parseInt(value.trim(), 10);

  return	isFinite(n) && n > 0
			?	n
			:	0;
}

export function hasValidWindow(from?: string, to?: string): boolean {
  return	parseHHMM(from) !== null &&
			parseHHMM(to)   !== null;
}

export function isHalfWindow(from?: string, to?: string): boolean {
  const hasFrom = !!from && from !== UrlDisabled;
  const hasTo   = !!to   && to   !== UrlDisabled;

  if (!hasFrom && !hasTo)
    return false;

  return !hasValidWindow(from, to);
}

export function isScreenTimeConfigured(quota?: string, from?: string, to?: string): boolean {
  return	parseQuotaMinutes(quota) > 0 ||
			hasValidWindow(from, to);
}

// Does THIS scope set a limit of its own? Only the four limit fields count. A
// room that picked its own title or background colour has still set no limit
// and still follows the project's — saying otherwise hides the one warning that
// explains why the room is restricted.
export function hasOwnScreentimeLimit(fields?: UrlInfo[]): boolean {
  const list = fields || [];

  return screentimeLimitFields.some((field: string) => {
    const val = getField(list, field);

    return	val != "" &&
			val != UrlDisabled;
  });
}


// ---------------------------------------------------------------------------
// Blocked-screen display: details / colour / title.
//
// All three are pure pass-through. We store them, resolve project -> room and
// hand them to the TV, which is the only thing that gives them meaning — no
// verdict on this side reads them. So nothing here may reject a value: every
// parse degrades to the default rather than failing, because a mistyped colour
// must never take the blocked screen down.
// ---------------------------------------------------------------------------

// The TV owns the real default blue. This copy exists ONLY to give the picker a
// swatch and the preview something to paint while the field is unset — it is
// never written to the field on its own. A native <input type="color"> is never
// visually empty, so "unset" has to be tracked apart from what the control
// happens to be rendering, or every visit to this form would silently save a
// colour nobody chose.
export const screentimeDefaultColour = "#0000FF";

export interface ScreentimeRGB {
  r:					number;		// 0-255
  g:					number;
  b:					number;
}

function clampChannel(n: number): number {
  if (!isFinite(n))
    return 0;

  return Math.min(255, Math.max(0, Math.round(n)));
}

// Deliberately generous: #RGB, #RRGGBB, a bare RRGGBB and rgb(r, g, b) all
// parse. Anything else is null, which every caller reads as "use the default".
export function parseScreentimeColour(value?: string): ScreentimeRGB | null {
  if (	!value ||
		value === UrlDisabled
	 )
    return null;

  const text = value.trim();

  const rgb = /^rgba?\(\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*,\s*([0-9]{1,3})\s*(?:,[^)]*)?\)$/i.exec(text);
  if (rgb) {
    const r = parseInt(rgb[1], 10);
    const g = parseInt(rgb[2], 10);
    const b = parseInt(rgb[3], 10);

    if (	r > 255 ||
			g > 255 ||
			b > 255
		 )
      return null;

    return { r: r, g: g, b: b };
  }

  const hex = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(text);
  if (!hex)
    return null;

  const digits =	hex[1].length == 3
					?	hex[1].split('').map((c: string) => c + c).join('')
					:	hex[1];

  return {
    r:	parseInt(digits.slice(0, 2), 16),
    g:	parseInt(digits.slice(2, 4), 16),
    b:	parseInt(digits.slice(4, 6), 16),
  };
}

export function toScreentimeHex(rgb: ScreentimeRGB): string {
  const pad = (n: number) => clampChannel(n).toString(16).padStart(2, '0');

  return ('#' + pad(rgb.r) + pad(rgb.g) + pad(rgb.b)).toUpperCase();
}

// "" for unset OR unparseable — both mean "the TV uses its own default".
export function normalizeScreentimeColour(value?: string): string {
  const rgb = parseScreentimeColour(value);

  return	rgb
			?	toScreentimeHex(rgb)
			:	"";
}

// What to PAINT for a given stored value. Never "", so a swatch or preview can
// use it directly.
export function screentimeColourOrDefault(value?: string): string {
  return normalizeScreentimeColour(value) || screentimeDefaultColour;
}

// Tri-state on the wire, boolean to the TV: "0" hides the panel, anything else
// shows it, and EMPTY shows it too. That last part is what keeps a project
// predating these keys rendering exactly as it does today.
export function parseScreentimeDetails(value?: string): boolean {
  if (	!value ||
		value === UrlDisabled
	 )
    return true;

  return value.trim() != "0";
}

export function hasLegacyScreentimeAcl(acls?: UrlInfo[]): boolean {
  const list = acls || [];

  return screentimeFields.some((field: string) => getField(list, field) == "1");
}

export function canOverrideScreentimeField(acls: UrlInfo[] | undefined, field: string): boolean {
  const list = acls || [];
  const explicit = getField(list, screentimeAclKey);

  if (explicit == "1")
    return true;

  if (explicit != "")
    return false;

  return getField(list, field) == "1";
}

export function canOverrideScreentime(acls?: UrlInfo[]): boolean {
  const list = acls || [];
  const explicit = getField(list, screentimeAclKey);

  if (explicit == "1")
    return true;

  if (explicit != "")
    return false;

  return hasLegacyScreentimeAcl(list);
}

export function resolveScreentimeField(
  roomFields:		UrlInfo[] | undefined,
  projFields:		UrlInfo[] | undefined,
  field:			string,
): string {
  const projValue = getField(projFields || [], field);

  if (!roomFields)
    return projValue;

  const roomValue = getField(roomFields, field);

  return	roomValue == "" || roomValue == UrlDisabled
			?	projValue
			:	roomValue;
}

export interface ResolvedScreenTime {
  quota:				string;
  period:				string;
  from:					string;
  to:					string;
  message:				string;
  title:				string;		// "" = the TV's built-in heading
  colour:				string;		// "" = the TV's default blue
  details:				boolean;	// false = paint the colour and nothing else
  configured:			boolean;
}

export function resolveScreenTime(
  roomFields:		UrlInfo[] | undefined,
  projFields:		UrlInfo[] | undefined,
): ResolvedScreenTime {
  const resolve = (field: string) => resolveScreentimeField(roomFields, projFields, field);

  const quota		= resolve(screentimeFields[0]);
  const period		= resolve(screentimeFields[1]);
  const from		= resolve(screentimeFields[2]);
  const to			= resolve(screentimeFields[3]);
  const message		= resolve(screentimeFields[4]);
  const details		= resolve(screentimeDetailsField);
  const colour		= resolve(screentimeColourField);
  const title		= resolve(screentimeTitleField);

  return {
    quota:			quota,
    period:			period,
    from:			from,
    to:				to,
    message:		message,
    title:			title,
    // Normalized, not raw: an unparseable stored colour has to read the same as
    // an unset one everywhere downstream, exactly as the TV treats it.
    colour:			normalizeScreentimeColour(colour),
    details:		parseScreentimeDetails(details),
    configured:		isScreenTimeConfigured(quota, from, to),
  };
}

function normalizeIntervals(raw: any): ScreenTimeInterval[] {
  if (!Array.isArray(raw))
    return [];

  return raw
    .filter((iv: any) => iv && typeof iv === "object")
    .map((iv: any) => {
      const raw_from	= Array.isArray(iv) ? iv[0] : (iv.from ?? iv.start ?? iv.begin);
      const raw_to		= Array.isArray(iv) ? iv[1] : (iv.to   ?? iv.end);

      const from	= toClock(raw_from);
      const to		= toClock(raw_to);

      if (	(from === "" || to === "") &&
			!warnedIntervalShape
		 ) {
        warnedIntervalShape = true;
        console.warn(
          "screentime: could not decode an interval — expected {from,to} as \"HH:MM\". Raw value:",
          iv
        );
      }

      return { from: from, to: to };
    })
    .filter((iv: ScreenTimeInterval) => iv.from !== "" && iv.to !== "");
}

let warnedIntervalShape = false;

export function toClock(value: any): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  const fromMinutes = (mins: number) =>
    pad(Math.floor(mins / 60) % 24) + ':' + pad(Math.floor(mins) % 60);

  if (typeof value === "string") {
    const text = value.trim();
    if (text === "")
      return "";

    const hhmm = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(text);
    if (hhmm) {
      const hours = parseInt(hhmm[1], 10);
      const mins  = parseInt(hhmm[2], 10);

      return	hours <= 23 && mins <= 59
				?	pad(hours) + ':' + pad(mins)
				:	"";
    }

    // ISO-ish timestamp.
    const ts = Date.parse(text);
    if (!isNaN(ts)) {
      const d = new Date(ts);
      return pad(d.getHours()) + ':' + pad(d.getMinutes());
    }

    return "";
  }

  if (	typeof value === "number" &&
		isFinite(value) &&
		value >= 0
	 ) {
    if (value < minutesPerDay)			// minutes since midnight
      return fromMinutes(value);

    if (value < minutesPerDay * 60)		// seconds since midnight
      return fromMinutes(value / 60);

    return fromMinutes(new Date(value * 1000).getHours() * 60 +
                       new Date(value * 1000).getMinutes());
  }

  return "";
}


function normalizeReport(body: any): ScreenTimeReport {
  const days = Array.isArray(body?.days)
    ?	body.days
        .filter((d: any) => d && typeof d === "object")
        .map((d: any) => ({
          day:			typeof d.day === "string" ? d.day : "",
          seconds:		typeof d.seconds === "number" ? d.seconds : 0,
          intervals:	normalizeIntervals(d.intervals),
        }))
    :	[];

  return {
    project_id:			typeof body?.project_id === "string" ? body.project_id : "",
    room_no:			typeof body?.room_no    === "string" ? body.room_no    : "",
    period:				normalizeScreentimePeriod(body?.period),
    quota_seconds:		typeof body?.quota_seconds     === "number" ? body.quota_seconds     : 0,
    used_seconds:		typeof body?.used_seconds      === "number" ? body.used_seconds      : 0,
    remaining_seconds:	typeof body?.remaining_seconds === "number" ? body.remaining_seconds : 0,
    blocked:			body?.blocked === true,
    days:				days,
  };
}

export async function fetchScreenTime(req: ScreenTimeRequest): Promise<ScreenTimeResult> {
  const params = new URLSearchParams();

  params.set("project_id",	req.projectId);
  params.set("room_no",		req.roomNo);
  params.set("days",		clampScreentimeDays(req.days).toString());

  if (req.period)
    params.set("period", normalizeScreentimePeriod(req.period));

  let response: Response;

  try {
    response = await fetch(`${screenTimeUrl}?${params.toString()}`, {
      method:		"GET",
      credentials:	"include",
    });
  } catch (e) {
    console.error("fetchScreenTime/failed:", e);
    return { kind: "error", status: 0, message: "Network error" };
  }

  if (response.ok) {
    try {
      return { kind: "ok", report: normalizeReport(await response.json()) };
    } catch (e) {
      console.error("fetchScreenTime/malformed body:", e);
      return { kind: "error", status: response.status, message: "Malformed response" };
    }
  }

  let text = "";
  try {
    text = (await response.text()).trim();
  } catch (e) { /* body already consumed or empty */ }

  switch (response.status) {
  case 401:
    store.dispatchStatus(0);
    return { kind: "unauthorized" };

  case 403:
    // Every refusal reads the same to us: the ACL is the only gate the UI
    // knows about, so we say "no access" and keep the panel in place rather
    // than pulling the tab out from under the click.
    return { kind: "no_project_access" };

  case 404:
    return { kind: "not_found" };

  case 400:
  case 405:
    return { kind: "bad_request", status: response.status };

  case 503:
    return { kind: "disabled" };

  default:
    return {
      kind:		"error",
      status:	response.status,
      message:	text || response.statusText || `Request failed (${response.status})`,
    };
  }
}

export type ScreenTimeResetResult =
  | { kind: "ok" }										// 204 No Content
  | { kind: "unauthorized" }							// 401
  | { kind: "no_project_access" }						// 403 no access to this project
  | { kind: "managers_disabled" }						// 403 Manager without the enable-screentime grant
  | { kind: "not_found" }								// 404
  | { kind: "bad_request";			status: number }	// 400 / 405
  | { kind: "disabled" }								// 503 recording off server-wide
  | { kind: "error";				status: number; message: string };	// 500 / network

export async function resetScreenTime(projectId: string, roomNo: string): Promise<ScreenTimeResetResult> {
  const params = new URLSearchParams();

  params.set("project_id",	projectId);
  params.set("room_no",		roomNo);

  let response: Response;

  try {
    response = await fetch(`${screenTimeUrl}?${params.toString()}`, {
      method:		"DELETE",
      credentials:	"include",
    });
  } catch (e) {
    console.error("resetScreenTime/failed:", e);
    return { kind: "error", status: 0, message: "Network error" };
  }

  if (response.ok)		// 204, no body to read
    return { kind: "ok" };

  let text = "";
  try {
    text = (await response.text()).trim();
  } catch (e) { /* body already consumed or empty */ }

  switch (response.status) {
  case 401:
    store.dispatchStatus(0);
    return { kind: "unauthorized" };

  case 403:
    // Two causes here: destroying history asks for the `enable-screentime`
    // grant, which the server words with "manager". Admins are not gated on it.
    if (/manager/i.test(text))
      return { kind: "managers_disabled" };

    return { kind: "no_project_access" };

  case 404:
    return { kind: "not_found" };

  case 400:
  case 405:
    return { kind: "bad_request", status: response.status };

  case 503:
    return { kind: "disabled" };

  default:
    return {
      kind:		"error",
      status:	response.status,
      message:	text || response.statusText || `Request failed (${response.status})`,
    };
  }
}

export function toDayKey(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');

  return	date.getFullYear() + '-' +
			pad(date.getMonth() + 1) + '-' +
			pad(date.getDate());
}

export function buildDayRange(days: number, today?: Date): string[] {
  const count = clampScreentimeDays(days);
  const end = today ? new Date(today.getTime()) : new Date();
  const out: string[] = [];

  for (let i = 0; i < count; i++) {
    const d = new Date(end.getTime());
    d.setDate(d.getDate() - i);
    out.push(toDayKey(d));
  }

  return out;
}

export function mergeDays(range: string[], days: ScreenTimeDay[]): ScreenTimeDay[] {
  const byDay = new Map<string, ScreenTimeDay>();
  for (const day of days) {
    if (day.day)
      byDay.set(day.day, day);
  }

  const filled: ScreenTimeDay[] = range.map((key: string) =>
    byDay.get(key) || { day: key, seconds: 0, intervals: [] }
  );

  const inRange = new Set(range);
  const extra = days.filter((d: ScreenTimeDay) => d.day && !inRange.has(d.day));

  return [...filled, ...extra].sort((a: ScreenTimeDay, b: ScreenTimeDay) =>
    a.day == b.day
    ?	0
    :	(a.day > b.day ? -1 : 1)		// newest first
  );
}

export function intervalMinutes(iv: ScreenTimeInterval): number {
  const from = parseHHMM(iv.from);
  const to   = parseHHMM(iv.to);

  if (	from === null ||
		to === null
	 )
    return 0;

  const end = to < from ? minutesPerDay - 1 : to;

  return end - from + 1;
}

export function formatScreenTimeDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds / 60));
  const hours = Math.floor(total / 60);
  const mins  = total % 60;

  if (	hours > 0 &&
		mins > 0
	 )
    return `${hours} h ${mins} m`;

  if (hours > 0)
    return `${hours} h`;

  return `${mins} m`;
}

export interface IntervalBar {
  left:					number;		// percent of the 24-hour track
  width:				number;		// percent of the 24-hour track
}

export function intervalBar(iv: ScreenTimeInterval): IntervalBar | null {
  const from = parseHHMM(iv.from);
  if (from === null)
    return null;

  const mins = intervalMinutes(iv);
  if (mins <= 0)
    return null;

  return {
    left:		(from / minutesPerDay) * 100,
    width:		(mins / minutesPerDay) * 100,
  };
}

export function nowOnDayPercent(now?: Date): number {
  const d = now || new Date();

  return ((d.getHours() * 60 + d.getMinutes()) / minutesPerDay) * 100;
}

export function periodMinutes(period: string): number {
  switch (normalizeScreentimePeriod(period)) {
  case "week":
    return 7 * minutesPerDay;
  case "month":
    return 31 * minutesPerDay;
  case "year":
    return 366 * minutesPerDay;
  default:
    return minutesPerDay;
  }
}
